import * as React from 'react';

import { apiClient, ApiRequestError } from '~/lib/api';
import type { GuestSession, GuestSessionResponse } from '~/types';
import { GuestSessionResponseSchema } from '~/types/schemas';

/**
 * sessionStorage key — scoped to a single browser tab, lost on close.
 * Versioned so a wire-shape change can be force-rotated by bumping the suffix.
 *
 * Bumped to v2 when guest JWTs gained iss/aud claims. A tab holding a v1 token
 * from a pre-bump bundle that survives to a post-bump bundle won't find a v2
 * entry and will issue a fresh session on its next guest-authed call — no
 * 401 → renew → reject → visible-restart round trip required for that
 * deploy-edge case.
 */
const STORAGE_KEY = 'play.guestSession.v2';

interface StoredSession extends GuestSession {
  /** Keys used to recognize a cache hit. Re-issued when either changes. */
  episodeId: string;
  slug: string;
}

function readStoredSession(): StoredSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

function writeStoredSession(session: StoredSession): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Quota or privacy mode — fall back to in-memory state only.
  }
}

function clearStoredSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function toSession(response: GuestSessionResponse): GuestSession {
  // sessionRef === guestSessionId === the JWT `jti`. The wire response uses
  // `guestSessionId`; we surface `sessionRef` as a stable alias for the
  // claim-handoff URL parameter.
  return {
    token: response.token,
    guestSessionId: response.guestSessionId,
    sessionRef: response.guestSessionId,
    expiresAt: response.expiresAt,
    episodeId: response.episodeId,
    landingPageId: response.landingPageId,
    organizationId: response.organizationId,
  };
}

export interface UseGuestTokenResult {
  /** Active session — null while issuing or after a hard expiry. */
  session: GuestSession | null;
  isLoading: boolean;
  error: Error | null;
  /**
   * Update the stored session — call after a successful renewal in the API
   * client wrapper. The hook re-renders consumers with the new token.
   * NOTE: the server may CAP `expiresAt` on renewal to the underlying row
   * lifetime — callers must use whatever the server returns and never assume
   * a fresh full TTL.
   */
  updateSession: (next: GuestSession) => void;
  /** Wipe local state — used by the visible-restart flow. */
  clear: () => void;
}

/**
 * Lazy-issue a guest JWT for the given (slug, episodeId) pair.
 *
 * - On first mount, checks `sessionStorage` for a matching session. If found
 *   and not expired, reuses it without a network call.
 * - Otherwise POSTs `/v1/public/guest-sessions/` with `{ slug, episodeId }`
 *   and caches the result. Both inputs are REQUIRED — the server resolves
 *   the landing page from the slug, then validates the episode belongs to
 *   that landing page's target.
 * - Does NOT auto-renew preemptively — renewal is reactive on 401 inside
 *   `apiClientWithGuestAuth`.
 *
 * Show-type landing pages must only call this hook AFTER an episode has been
 * selected (i.e. on the game view, not on the landing/episodes view).
 */
export function useGuestToken(
  slug: string | null,
  episodeId: string | null,
): UseGuestTokenResult {
  const [session, setSession] = React.useState<GuestSession | null>(() => {
    const stored = readStoredSession();
    if (!stored) return null;
    if (stored.episodeId !== episodeId || stored.slug !== slug) return null;
    return stored;
  });
  const [error, setError] = React.useState<Error | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  // Guards against the strict-mode double effect issuing two sessions.
  const inflightKey = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!slug || !episodeId) return;

    const key = `${slug}::${episodeId}`;

    // Already have a valid session for this pair — nothing to do.
    if (session && session.episodeId === episodeId && (session as StoredSession).slug === slug) {
      return;
    }

    // Avoid double-issue under React strict mode (effect runs twice).
    if (inflightKey.current === key) return;
    inflightKey.current = key;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const raw = await apiClient<unknown>('/v1/public/guest-sessions/', {
          method: 'POST',
          body: { slug, episodeId },
        });
        const parsed = GuestSessionResponseSchema.parse(raw);
        if (cancelled) return;
        const next = toSession(parsed);
        const stored: StoredSession = {
          ...next,
          episodeId,
          slug,
        };
        writeStoredSession(stored);
        setSession(stored);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          inflightKey.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
      // Free the inflight slot so a later mount (or the strict-mode remount)
      // can start a fresh issue. Without this, the cleanup-then-remount
      // sequence in dev leaves `inflightKey === key` while `setIsLoading(true)`
      // has already been called by mount 1, so mount 2 short-circuits and the
      // hook is stuck in the loading state forever.
      inflightKey.current = null;
    };
    // We intentionally exclude `session` — re-running on every session change
    // would loop. The check above handles the cache-hit case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, episodeId]);

  const updateSession = React.useCallback(
    (next: GuestSession) => {
      // Preserve the (slug, episodeId) cache keys across renewals. The server
      // does not echo `slug` in the renew response, so we keep the value used
      // to mint the session in the first place.
      const previous = readStoredSession();
      const slugForKey = previous?.slug ?? '';
      const stored: StoredSession = {
        ...next,
        episodeId: next.episodeId,
        slug: slugForKey,
      };
      writeStoredSession(stored);
      setSession(stored);
    },
    [],
  );

  const clear = React.useCallback(() => {
    clearStoredSession();
    setSession(null);
  }, []);

  return { session, isLoading, error, updateSession, clear };
}

// Re-export for tests / call sites that need raw IO.
export const __internal = {
  STORAGE_KEY,
  readStoredSession,
  writeStoredSession,
  clearStoredSession,
  toSession,
  // The wrapper exists so tests can assert that ApiRequestError is the
  // expected error type without re-importing from `~/lib/api`.
  ApiRequestError,
};
